import { useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';
import { useCashListConfiguration } from '@pflegenavi/frontend/api-nursing-home';
import type { UrlFilters } from '@pflegenavi/web-components';
import {
  urlDateRange,
  urlString,
  useUrlFilters,
} from '@pflegenavi/web-components';
const TABS = [
  'all',
  'mismatch',
  'mismatchRisk',
  'deposit',
  'withdrawal',
  'transfer',
  'adjustment',
] as const;

export type TransactionGroupTab = (typeof TABS)[number];

// `cashListId` keeps existing deep links to the page working.
const CODECS = {
  cashList: urlString('cashListId'),
  updateDate: urlDateRange('from', 'to'),
  tab: urlString('tab'),
};

export type TransactionGroupFilterKey = keyof typeof CODECS;

export interface TransactionGroupFilters {
  /** Falls back to the configured cash list: one is always selected. */
  cashListId: string | undefined;
  tab: TransactionGroupTab;
  /** ISO datetime, inclusive. */
  updateDateFrom: string | undefined;
  /** ISO datetime, exclusive: the start of the day after `to`. */
  updateDateTo: string | undefined;
}

export interface TransactionGroupFiltersState
  extends UrlFilters<typeof CODECS> {
  filters: TransactionGroupFilters;
  cashLists: Array<{ id: string; name: string }>;
}

const isTab = (value: string | undefined): value is TransactionGroupTab =>
  TABS.some((tab) => tab === value);

export function useTransactionGroupFilters(): TransactionGroupFiltersState {
  const urlFilters = useUrlFilters(CODECS);
  const { values } = urlFilters;
  const { cashLists, selectedCashListId } = useCashListConfiguration();

  const filters = useMemo<TransactionGroupFilters>(
    () => ({
      cashListId: values.cashList ?? selectedCashListId,
      tab: isTab(values.tab) ? values.tab : 'all',
      updateDateFrom: values.updateDate?.from?.toISOString(),
      updateDateTo: values.updateDate?.to
        ? startOfDay(addDays(values.updateDate.to, 1)).toISOString()
        : undefined,
    }),
    [values, selectedCashListId]
  );

  return useMemo(
    () => ({ ...urlFilters, filters, cashLists }),
    [urlFilters, filters, cashLists]
  );
}
